import React from "react";
import type { ParsedCountry } from "../../types/PhoneInput";
import CountryDropdown from "./CountryDropdown";
import { usePhoneInput } from "../../hooks/usePhone";

interface PhoneInputProps {
  value?: string;
  onChange?: (
    value: string,
    formatted: string | null,
    country: ParsedCountry | null
  ) => void;
  onCountryChange?: (country: ParsedCountry | null) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: string | undefined;
  isValid?: boolean;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  placeholder?: string;
  defaultCountry?: string;
  ariaLabel?: string;
  showFormatted?: boolean;
  showInternational?: boolean;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value = "",
  onChange,
  onCountryChange,
  onBlur,
  error: _externalError,
  isValid: _externalIsValid,
  disabled = false,
  required = false,
  id = "phone-input",
  defaultCountry = "de",
  ariaLabel = "Phone number",
}) => {
  const {
    phone,
    setPhone,
    country,
    setCountry,
    formatted,
    isValid,
    error,
    expectedLengths,
    allCountries,
  } = usePhoneInput({
    defaultCountry: defaultCountry as any,
    onPhoneChange: (phoneValue, formattedValue, countryValue) => {
      onChange?.(phoneValue, formattedValue, countryValue);
    },
  });

  // Set initial value if provided
  React.useEffect(() => {
    if (value && value !== phone) {
      setPhone(value);
    }
  }, [value]);

  const handleCountryChange = (selectedCountry: ParsedCountry | null) => {
    if (selectedCountry) {
      setCountry(selectedCountry);
      setPhone(""); // ✅ Clear phone when country changes
      onCountryChange?.(selectedCountry);
    }
  };

  // ✅ Check if country has validation pattern
  const hasPattern = (c: ParsedCountry | null): boolean => {
    if (!c) return false;
    return c.format !== undefined && c.format !== null && c.format !== "";
  };

  // ✅ Get max digits (or use fallback for no-pattern countries)
  const getMaxDigits = (): number | null => {
    if (!country || !expectedLengths || expectedLengths.length === 0) {
      return null; // No limit for countries without pattern
    }
    return Math.max(...expectedLengths);
  };

  const displayPhoneValue = (): string => {
    if (!country || !hasPattern(country) || !country.format) {
      return phone; // ✅ No formatting for no-pattern countries
    }

    const format = country.format;

    // If format is object, just return phone (no simple formatting available)
    if (typeof format !== "string") {
      return phone;
    }

    let result = "";
    let phoneIndex = 0;

    for (let i = 0; i < format.length; i++) {
      const formatChar = format[i];
      if (formatChar === ".") {
        if (phoneIndex < phone.length) {
          result += phone[phoneIndex];
          phoneIndex++;
        } else {
          result += ".";
        }
      } else {
        result += formatChar;
      }
    }

    return result;
  };

  return (
    <div className="phone__input__box ">
      <label htmlFor={id} className="input__label">
        Phone Number
      </label>

      <div
        className={`phone-input ${error ? "phone-input--error" : ""} ${
          disabled ? "phone-input--disabled" : ""
        }`}
      >
        <CountryDropdown
          countries={allCountries}
          selectedCountry={country}
          onSelect={handleCountryChange}
          disabled={disabled}
          searchable={true}
        />

        <input
          id={id}
          type="tel"
          className="phone-input__field"
          value={displayPhoneValue()}
          onChange={(e: React.FocusEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            let digitsOnly = newValue.replace(/\D/g, "");

            const currentDisplay = displayPhoneValue();
            if (currentDisplay.length > newValue.length) {
              digitsOnly = digitsOnly.slice(0, -1);
            }

            const maxDigits = getMaxDigits();
            if (maxDigits !== null) {
              if (digitsOnly.length > maxDigits) {
                return;
              }
            } else {
              if (digitsOnly.length > 15) {
                // Fallback limit for no-pattern countries
                return;
              }
            }

            setPhone(digitsOnly);
            onChange?.(digitsOnly, formatted || null, country);
          }}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          aria-label={ariaLabel}
          aria-describedby={error ? `${id}-error` : undefined}
          aria-invalid={!!error}
          inputMode="numeric"
        />
      </div>

      {error && hasPattern(country) && (
        <p
          id={`${id}-error`}
          className="phone-input__error phone-input__error--show"
          role="alert"
        >
          {error}
        </p>
      )}

      {!error && phone && !isValid && expectedLengths.length > 0}
    </div>
  );
};

export default PhoneInput;
